import React, { useState, useEffect } from 'react';
import { visiMisiService } from '../../../services/visiMisiService';
import {
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaCheckCircle,
    FaExclamationTriangle, FaEye, FaEyeSlash, FaMinus
} from 'react-icons/fa';

const ManajemenVisiMisi = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const [formData, setFormData] = useState({
        judul_utama: '',
        periode: '',
        visi: '',
        misi: [''],
        deskripsi: '',
        status: 'publish',
    });

    const loadData = async () => {
        setLoading(true);
        const result = await visiMisiService.getVisiMisiList();
        if (result.success) {
            setDataList(result.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            judul_utama: '',
            periode: '',
            visi: '',
            misi: [''],
            deskripsi: '',
            status: 'publish',
        });
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            judul_utama: item.judul_utama || '',
            periode: item.periode || '',
            visi: item.visi || '',
            misi: item.misi && item.misi.length > 0 ? item.misi : [''],
            deskripsi: item.deskripsi || '',
            status: item.status || 'publish',
        });
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleMisiChange = (index, value) => {
        const newMisi = [...formData.misi];
        newMisi[index] = value;
        setFormData(prev => ({ ...prev, misi: newMisi }));
    };

    const addMisi = () => {
        setFormData(prev => ({ ...prev, misi: [...prev.misi, ''] }));
    };

    const removeMisi = (index) => {
        if (formData.misi.length <= 1) return;
        const newMisi = formData.misi.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, misi: newMisi }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Filter misi kosong
        const cleanMisi = formData.misi.filter(m => m.trim() !== '');
        if (cleanMisi.length === 0) {
            showNotif('Minimal satu misi harus diisi', 'error');
            return;
        }

        const submitData = {
            ...formData,
            misi: cleanMisi,
        };

        setSubmitting(true);
        let result;
        if (editing) {
            result = await visiMisiService.updateVisiMisi(editing.id, submitData);
        } else {
            result = await visiMisiService.createVisiMisi(submitData);
        }

        if (result.success) {
            showNotif(result.data?.message || 'Data berhasil disimpan');
            setShowForm(false);
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await visiMisiService.deleteVisiMisi(deleteConfirm.id);
        if (result.success) {
            showNotif('Data berhasil dihapus');
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Visi & Misi</h2>
                    <p className="text-gray-600">Kelola data visi dan misi instansi</p>
                </div>
                <button onClick={handleCreate} className="btn-primary flex items-center">
                    <FaPlus className="mr-2" /> Tambah Data
                </button>
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2" />
                        : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr className="text-left text-sm text-gray-500">
                            <th className="p-4">Periode</th>
                            <th className="p-4">Visi</th>
                            <th className="p-4">Jumlah Misi</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Diperbarui</th>
                            <th className="p-4 text-center">Aksi</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="p-8 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                </td>
                            </tr>
                        ) : dataList.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="p-8 text-center text-gray-500">
                                    Belum ada data visi misi
                                </td>
                            </tr>
                        ) : (
                            dataList.map((item) => (
                                <tr key={item.id} className="border-b hover:bg-gray-50">
                                    <td className="p-4 font-semibold text-[#0F4C3A]">{item.periode}</td>
                                    <td className="p-4 text-sm text-gray-700 max-w-md">
                                        <p className="line-clamp-2">{item.visi}</p>
                                    </td>
                                    <td className="p-4">
                                        <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-sm">
                                            {item.misi?.length || 0} misi
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.status === 'publish'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                            {item.status === 'publish' ? 'Publish' : 'Draft'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm text-gray-500">
                                        {formatDate(item.updated_at)}
                                    </td>
                                    <td className="p-4">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                title="Edit"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => setDeleteConfirm(item)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                title="Hapus"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    {editing ? 'Edit Visi & Misi' : 'Tambah Visi & Misi'}
                                </h3>
                                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {/* Judul Utama */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Judul Utama (Tagline)
                                    </label>
                                    <input
                                        type="text"
                                        name="judul_utama"
                                        value={formData.judul_utama}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Contoh: MEMBANGUN BERSAMA, MELAYANI SEPENUH HATI"
                                    />
                                </div>

                                {/* Periode */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Periode <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="periode"
                                        value={formData.periode}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Contoh: 2025 - 2030"
                                        required
                                    />
                                </div>

                                {/* Visi */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Visi <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="visi"
                                        value={formData.visi}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Tuliskan visi instansi..."
                                        required
                                    />
                                </div>

                                {/* Misi - Dynamic */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Misi <span className="text-red-500">*</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={addMisi}
                                            className="text-sm text-[#2E7D32] hover:text-[#4CAF50] font-medium flex items-center gap-1"
                                        >
                                            <FaPlus size={12} /> Tambah Misi
                                        </button>
                                    </div>

                                    <div className="space-y-2">
                                        {formData.misi.map((misi, index) => (
                                            <div key={index} className="flex gap-2 items-start">
                                                <span className="w-8 h-10 flex items-center justify-center bg-[#0F4C3A] text-white rounded-lg font-bold text-sm flex-shrink-0">
                                                    {index + 1}
                                                </span>
                                                <input
                                                    type="text"
                                                    value={misi}
                                                    onChange={(e) => handleMisiChange(index, e.target.value)}
                                                    className="flex-1 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                                    placeholder={`Misi ke-${index + 1}`}
                                                />
                                                {formData.misi.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeMisi(index)}
                                                        className="p-3 text-red-500 hover:bg-red-50 rounded-lg"
                                                        title="Hapus misi ini"
                                                    >
                                                        <FaMinus />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Deskripsi */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Deskripsi Tambahan
                                    </label>
                                    <textarea
                                        name="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Deskripsi tambahan (opsional)"
                                    />
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    >
                                        <option value="publish">Publish (Tampil di Website)</option>
                                        <option value="draft">Draft (Simpan Saja)</option>
                                    </select>
                                </div>

                                {/* Buttons */}
                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Menyimpan...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Data?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus data visi misi periode <strong>{deleteConfirm.periode}</strong>.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenVisiMisi;